import { UserContext, BusinessUnitComponent } from '@zola-helpers/client/dist/es/@types';
import insertInArrayIf from '@zola-helpers/client/dist/es/util/insertInArrayIf';
import { getMobileBoutiqueMenuData } from 'components/nav/data/weddingBoutiqueLinks';
import getMobileAlbumsMenuData from './mobileNavData/getMobileAlbumsMenuData';
import getMobileExpertAdviceMenuData from './mobileNavData/getMobileExpertAdviceMenuData';
import getMobileGuestListMenuData from './mobileNavData/getMobileGuestListMenuData';
import getMobileInspirationMenuData from './mobileNavData/getMobileInspirationMenuData';
import getMobilePaperMenuData from './mobileNavData/getMobilePaperMenuData';
import getMobileRegistryMenuData from './mobileNavData/getMobileRegistryMenuData';
import getMobileVendorsMenuData from './mobileNavData/getMobileVendorsMenuData';
import getMobileWebsiteMenuData from './mobileNavData/getMobileWebsiteMenuData';
import getMobileYourWeddingMenuData from './mobileNavData/getMobileYourWeddingMenuData';

export type MobileNavLink = {
  title: string | JSX.Element;
  path: string;
  businessUnitComponent?: BusinessUnitComponent | string;
  target?: string;
  icon?: JSX.Element | string | null;
  isDisabled?: boolean;
};

export type MobileNavMenuLinkGroups = {
  title?: string;
  linkGroups: (MobileNavLink | MobileNavMenuLinkGroups)[][];
  path?: never;
  businessUnitComponent?: BusinessUnitComponent | SupplementalBusinessUnitComponent;
};

// TODO: Add these to zola-helpers
export enum SupplementalBusinessUnitComponent {
  ADVICE = 'ADVICE',
  BOUTIQUE = 'BOUTIQUE',
  CORE = 'CORE',
  HONEYMOONS = 'HONEYMOONS',
  YOUR_WEDDING = 'YOUR WEDDING',
}

const getMobileNavMenuData = (userContext: UserContext): MobileNavMenuLinkGroups[] => {
  const isGuest = Boolean(userContext.is_guest);

  const albumsParent = isGuest
    ? {
        title: 'Albums',
        path: '/wedding-albums/shop',
        businessUnitComponent: BusinessUnitComponent.WEDDING_ALBUM,
      }
    : {
        title: 'Albums',
        linkGroups: getMobileAlbumsMenuData(userContext),
        businessUnitComponent: BusinessUnitComponent.WEDDING_ALBUM,
      };

  const websiteParent = isGuest
    ? {
        title: 'Website',
        path: '/wedding-planning/website',
        businessUnitComponent: BusinessUnitComponent.WEDDING_WEBSITE,
      }
    : {
        title: 'Website',
        linkGroups: getMobileWebsiteMenuData(userContext),
        businessUnitComponent: BusinessUnitComponent.WEDDING_WEBSITE,
      };

  const guestListParent = isGuest
    ? {
        title: 'Guest List',
        path: '/wedding-planning/guests',
        businessUnitComponent: BusinessUnitComponent.WEDDING_GUEST_LIST,
      }
    : {
        title: 'Guest List',
        linkGroups: getMobileGuestListMenuData(userContext),
        businessUnitComponent: BusinessUnitComponent.WEDDING_GUEST_LIST,
      };

  return [
    ...insertInArrayIf(!isGuest, {
      linkGroups: [
        [
          {
            title: 'Home',
            path: '/wedding/manage',
            businessUnitComponent: SupplementalBusinessUnitComponent.CORE,
          },
          {
            title: 'Your Wedding',
            path: '',
            linkGroups: getMobileYourWeddingMenuData(userContext),
            businessUnitComponent: SupplementalBusinessUnitComponent.YOUR_WEDDING,
          },
        ],
      ],
    }),
    {
      title: 'Shop',
      linkGroups: [
        [
          {
            title: 'Registry',
            linkGroups: getMobileRegistryMenuData(userContext),
            businessUnitComponent: BusinessUnitComponent.REGISTRY,
          },
          {
            title: 'Invites + Paper',
            linkGroups: getMobilePaperMenuData(userContext),
            businessUnitComponent: BusinessUnitComponent.INVITATIONS,
          },
          albumsParent,
          {
            title: 'Boutique',
            linkGroups: getMobileBoutiqueMenuData(),
            businessUnitComponent: SupplementalBusinessUnitComponent.BOUTIQUE,
          },
        ],
      ],
    },
    {
      title: 'Plan',
      linkGroups: [
        [
          {
            title: 'Venues & Vendors',
            linkGroups: getMobileVendorsMenuData(userContext),
            businessUnitComponent: BusinessUnitComponent.MARKETPLACE,
          },
          {
            title: 'Real Wedding Inspiration',
            path: isGuest ? '/inspiration' : undefined,
            linkGroups: isGuest ? [] : getMobileInspirationMenuData(userContext),
            businessUnitComponent: BusinessUnitComponent.INSPIRATION,
          },
          websiteParent,
          guestListParent,
          {
            title: 'Budget',
            path: '/wedding/manage/budget',
            businessUnitComponent: BusinessUnitComponent.BUDGET_TOOL,
          },
          {
            title: '1:1 Advisors',
            path: '/advisor',
          },
          {
            title: 'Expert Advice',
            linkGroups: getMobileExpertAdviceMenuData(userContext),
            businessUnitComponent: SupplementalBusinessUnitComponent.ADVICE,
          },
        ],
      ],
    },
  ];
};

export default getMobileNavMenuData;
